import React from "react";

export type ExtensionPointName = string;
export type ExtensionComponent = React.ComponentType<any>;

/**
 * Represents a registered extension component.
 * 
 * @property {ExtensionPointName} name - The extension point name this extension is registered to
 * @property {ExtensionComponent} component - The React component to render
 * @property {string} [componentName] - Optional unique name for the component
 * @property {boolean} [isOverride] - Whether this extension overrides existing extensions at this point
 * @property {number} [sortOrder] - Optional sort order (lower values render first). Defaults to 1000 if not specified.
 */
export type Extension = {
  name: ExtensionPointName;
  component: ExtensionComponent;
  componentName?: string;
  isOverride?: boolean;
  sortOrder?: number;
};

export interface ExtensionRegistry {
  [pointName: string]: Extension[];
}

/**
 * Predefined sort order constants for common extension positioning.
 * Extensions are rendered in ascending sort order (lower values render first).
 * 
 * Use these constants for consistency across plugins, or use custom numbers for fine-grained control.
 * 
 * @example
 * // Using constants (recommended for standard positions)
 * registerExtension("admin.dashboard", CriticalWidget, "Critical", false, EXTENSION_SORT_ORDER.VERY_HIGH);
 * 
 * @example
 * // Using custom numbers (for fine-grained control)
 * registerExtension("admin.dashboard", CustomWidget, "Custom", false, 250);
 */
export const EXTENSION_SORT_ORDER = {
  /** Render absolutely first (0) - for critical system-level extensions */
  FIRST: 0,
  /** Very high priority (100) - for important system features */
  VERY_HIGH: 100,
  /** High priority (300) - for important plugin features */
  HIGH: 300,
  /** Normal priority (1000) - default for most extensions */
  NORMAL: 1000,
  /** Low priority (1500) - for less important content */
  LOW: 1500,
  /** Very low priority (2000) - for supplemental/footer content */
  VERY_LOW: 2000,
  /** Render absolutely last (9999) - for debug/admin tools */
  LAST: 9999,
} as const;

/**
 * Default sort order for extensions when not explicitly specified.
 * Extensions are rendered in ascending sort order (lower values first).
 */
export const DEFAULT_SORT_ORDER = EXTENSION_SORT_ORDER.NORMAL;

const extensionRegistry: ExtensionRegistry = {};

/**
 * Gets a list of all registered extension point names.
 * Useful for debugging and discovering available extension points.
 * 
 * @returns Array of extension point names that have registered extensions
 * 
 * @example
 * // In browser console or component
 * const points = getRegisteredExtensionPoints();
 * console.log("Available extension points:", points);
 */
export function getRegisteredExtensionPoints(): string[] {
  return Object.keys(extensionRegistry).sort();
}

/**
 * Gets detailed information about all registered extensions.
 * Useful for debugging extension registration and sort order.
 * 
 * @returns Object mapping extension point names to their registered extensions
 * 
 * @example
 * // In browser console
 * const details = getExtensionDetails();
 * console.table(details["admin.dashboard"]);
 */
export function getExtensionDetails(): Record<string, Array<{
  componentName?: string;
  sortOrder: number;
  isOverride: boolean;
}>> {
  const details: Record<string, Array<{
    componentName?: string;
    sortOrder: number;
    isOverride: boolean;
  }>> = {};
  
  for (const [pointName, extensions] of Object.entries(extensionRegistry)) {
    details[pointName] = extensions.map(ext => ({
      componentName: ext.componentName,
      sortOrder: ext.sortOrder ?? DEFAULT_SORT_ORDER,
      isOverride: ext.isOverride ?? false,
    }));
  }
  
  return details;
}

/**
 * Registers a component to be rendered at a specific extension point.
 * 
 * @param {ExtensionPointName} pointName - The name of the extension point to register to
 * @param {ExtensionComponent} component - The React component to render
 * @param {string} [componentName] - Optional unique identifier for this component
 * @param {boolean} [isOverride=false] - Whether this component should override existing extensions
 * @param {number} [sortOrder] - Optional sort order (lower values render first). Defaults to 1000.
 * 
 * @example
 * // Register with default sort order (1000)
 * registerExtension("admin.dashboard", MyWidget, "MyWidget");
 * 
 * @example
 * // Register with high priority (renders early)
 * registerExtension("admin.dashboard", ImportantWidget, "ImportantWidget", false, 100);
 * 
 * @example
 * // Register with low priority (renders late)
 * registerExtension("admin.dashboard", LessImportantWidget, "LessImportantWidget", false, 2000);
 */
export function registerExtension(
  pointName: ExtensionPointName,
  component: ExtensionComponent,
  componentName?: string,
  isOverride: boolean = false,
  sortOrder?: number
) {
  if (!extensionRegistry[pointName]) {
    extensionRegistry[pointName] = [];
  }

  const existingIdx = extensionRegistry[pointName].findIndex(
    (ext) => ext.componentName === componentName
  );

  if (existingIdx !== -1) {
    if (isOverride) {
      // Replace the existing extension with the override
      extensionRegistry[pointName][existingIdx] = {
        name: pointName,
        component,
        componentName,
        isOverride: true,
        sortOrder: sortOrder ?? DEFAULT_SORT_ORDER,
      };
      return;
    } else if (extensionRegistry[pointName][existingIdx].isOverride) {
      // Do not allow non-override to replace an override
      return;
    } else {
      console.error(
        `Extension with name ${componentName} already exists at point ${pointName}`
      );
      return;
    }
  }

  extensionRegistry[pointName].push({
    name: pointName,
    component,
    componentName,
    isOverride,
    sortOrder: sortOrder ?? DEFAULT_SORT_ORDER,
  });
}

interface ExtensionRegistryContextType {
  getExtensions: (pointName: ExtensionPointName) => ExtensionComponent[];
  isRegistered: (pointName: ExtensionPointName) => boolean;
}

const ExtensionRegistryContext = React.createContext<ExtensionRegistryContextType | null>(null);

export const useExtensionRegistry = (): ExtensionRegistryContextType => {
  const context = React.useContext(ExtensionRegistryContext);
  if (!context) {
    throw new Error("useExtensionRegistry must be used within an ExtensionProvider");
  }
  return context;
};

interface ExtensionProviderProps {
  children: React.ReactNode;
}

// The provider that supplies registered extensions via context.
export const ExtensionProvider: React.FC<ExtensionProviderProps> = ({ children }) => {
  const extRegistry = extensionRegistry;

  const getExtensions = (pointName: ExtensionPointName): ExtensionComponent[] => {
    const extensions = extRegistry[pointName] || [];
    
    // If any override exists, only return the override(s)
    const overrides = extensions.filter((ext) => ext.isOverride);
    if (overrides.length > 0) {
      // Sort overrides by sortOrder (lower values first)
      return overrides
        .sort((a, b) => (a.sortOrder ?? DEFAULT_SORT_ORDER) - (b.sortOrder ?? DEFAULT_SORT_ORDER))
        .map((ext) => ext.component);
    }
    
    // Sort all extensions by sortOrder (lower values first)
    // Using stable sort - extensions with same sortOrder maintain registration order
    return extensions
      .sort((a, b) => (a.sortOrder ?? DEFAULT_SORT_ORDER) - (b.sortOrder ?? DEFAULT_SORT_ORDER))
      .map((ext) => ext.component);
  };

  const isRegistered = (pointName: ExtensionPointName): boolean => {
    return Object.keys(extensionRegistry || {}).includes(pointName);
  };

  return (
    <ExtensionRegistryContext.Provider value={{ getExtensions, isRegistered }}>
      {children}
    </ExtensionRegistryContext.Provider>
  );
};
